import React, {useCallback, useLayoutEffect, useRef} from 'react';

interface AutoFitSingleLineTextProps {
  text: string;
  className: string;
  containerClassName?: string;
}

export const calculateSingleLineTextScale = (
  availableWidth: number,
  contentWidth: number,
) => {
  if (
    !Number.isFinite(availableWidth) ||
    !Number.isFinite(contentWidth) ||
    availableWidth <= 0 ||
    contentWidth <= 0
  ) {
    return 1;
  }

  return Math.min(1, availableWidth / contentWidth);
};

export function AutoFitSingleLineText({
  text,
  className,
  containerClassName = '',
}: AutoFitSingleLineTextProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLSpanElement>(null);

  const fitText = useCallback(() => {
    const container = containerRef.current;
    const content = contentRef.current;

    if (!container || !content) {
      return;
    }

    content.style.transform = `scale(${calculateSingleLineTextScale(
      container.clientWidth,
      content.scrollWidth,
    )})`;
  }, []);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || !contentRef.current) {
      return undefined;
    }

    fitText();
    const frame = window.requestAnimationFrame?.(fitText);
    const resizeObserver =
      typeof ResizeObserver === 'function'
        ? new ResizeObserver(fitText)
        : undefined;

    resizeObserver?.observe(container);
    window.addEventListener?.('resize', fitText);

    let active = true;
    document.fonts?.ready.then(() => {
      if (active) {
        fitText();
      }
    });

    return () => {
      active = false;
      if (frame != null) {
        window.cancelAnimationFrame?.(frame);
      }
      resizeObserver?.disconnect();
      window.removeEventListener?.('resize', fitText);
    };
  }, [fitText, text]);

  return (
    <div
      ref={containerRef}
      className={`flex items-center justify-center ${containerClassName}`}>
      <span
        ref={contentRef}
        className={`inline-block origin-center whitespace-nowrap ${className}`}>
        {text}
      </span>
    </div>
  );
}
